import {put, takeEvery, takeLatest, select} from 'redux-saga/effects';
import {saveAs} from 'file-saver';
import moment from 'moment';
import {toast} from 'react-toastify';
import {
  GENERATE_REPORT,
  GENERATE_REPORT_SUCCESS,
  EXPORT_REPORTS,
  STOP_EXPORT_REPORTS_LOADING,
  STOP_REPORTS_LOADING,
  SET_REPORT_VISITED_STATISTIC,
  GENERATE_SPECIAL_ORDERS_REPORT,
  GENERATE_SPECIAL_ORDERS_REPORT_SUCCESS,
  RECALCULATE_COMMISSION_SAVED,
  DOWNLOAD_SELF_DELIVERY_INVOICE_ORDERS,
  DOWNLOAD_SELF_DELIVERY_INVOICE_ORDERS_DONE,
  OPEN_SELF_DELIVERY_INVOICE,
  OPEN_SELF_DELIVERY_INVOICE_DONE,
  SET_REPORT_LOCATION
} from 'store/reports/action';
import {
  getXlsReportsApi,
  generateReportApi,
  getReportsApi,
  getSpecialOrdersReportApi,
  addEmailToSpecialOrdersReportApi,
  exportSelfDeliveryInvoiceOrdersCsvApi,
  getSelfDeliveryInvoiceLinkApi
} from 'helpers/services/api/reports';
import {
  REPORT_TYPES,
  SELF_DELIVERY_DATE_FILTER,
  getSelfDeliveryInvoicesDateRange
} from 'helpers/constants/reports';
import {getStorageToken} from 'helpers/localStorage/authStorage';
import {
  getUserStatisticApi,
  postUserStatisticApi
} from 'helpers/services/api/user';
import {getSessionStorageItem} from 'helpers/sessionStorage';
import SESSION_SAUCE_EMPLOYEE_NAME from 'helpers/sessionStorage/constants';

const toTime = (date) => (date ? new Date(date).getTime() : 0);

// Groups per order date; in the charge-date view per (charge date, order date)
function aggregateOrdersByDay(orders, dateFilter) {
  const byChargeDate = dateFilter === SELF_DELIVERY_DATE_FILTER.CHARGE_DATE;
  const dayMap = {};
  orders.forEach((order) => {
    const date = order.orderDate;
    const key = byChargeDate ? `${order.chargeDate}|${date}` : date;
    if (!dayMap[key]) {
      dayMap[key] = {
        orderDate: date,
        chargeDate: order.chargeDate ?? null,
        chargeAmount: 0,
        deliveryFeesAndTipsPaidByCustomer: 0,
        deliveryFeeOnlyPaidByCustomer: 0,
        tipPaidByCustomer: 0,
        deliveryFeePaidByStore: 0,
        creditReimbursement: 0,
        commissionSaved: 0,
        orderSubtotal: 0
      };
    }
    const row = dayMap[key];
    row.chargeAmount += order.chargeAmount || 0;
    row.deliveryFeesAndTipsPaidByCustomer +=
      order.deliveryFeesAndTipsPaidByCustomer || 0;
    row.deliveryFeeOnlyPaidByCustomer +=
      order.deliveryFeeOnlyPaidByCustomer || 0;
    row.tipPaidByCustomer += order.tipPaidByCustomer || 0;
    row.deliveryFeePaidByStore += order.deliveryFeePaidByStore || 0;
    row.creditReimbursement += order.creditReimbursement || 0;
    row.commissionSaved += order.commissionSaved || 0;
    row.orderSubtotal += order.orderSubtotal || 0;
  });
  return Object.values(dayMap).sort(
    (a, b) =>
      (byChargeDate ? toTime(a.chargeDate) - toTime(b.chargeDate) : 0) ||
      toTime(a.orderDate) - toTime(b.orderDate)
  );
}

const selectReportRevision = (state) => state.reportsReducer.reportRevision;

// A response only applies while nothing newer happened: no other Generate and no
// clear (type or location switch) since it started. Otherwise it is dropped and
// the loading state is left to whatever changed it.
function* putIfStillCurrent(revision, locationId, action) {
  const currentRevision = yield select(selectReportRevision);
  if (currentRevision !== revision) return;

  // Location switched elsewhere while loading, with no clear yet: this is still
  // the latest request, so it settles the loading state but shows nothing.
  const currentLocationId = yield select((state) => state.user.locationId);
  if (locationId && currentLocationId !== locationId) {
    yield put(STOP_REPORTS_LOADING());
    return;
  }
  yield put(action);
}

function* stopLoadingIfStillCurrent(revision) {
  const currentRevision = yield select(selectReportRevision);
  if (currentRevision === revision) yield put(STOP_REPORTS_LOADING());
}

function* generateReport({payload}) {
  const revision = yield select(selectReportRevision);
  const {token} = getStorageToken();
  const dateRange = yield select((state) => state.reportsReducer.dateRange);
  const reportType = yield select((state) => state.reportsReducer.reportType);
  const selfDeliveryDateFilter = yield select(
    (state) => state.reportsReducer.selfDeliveryDateFilter
  );
  const salesBreakdownChannels = yield select(
    (state) => state.reportsReducer.salesBreakdownChannels
  );
  const salesBreakdownFulfillments = yield select(
    (state) => state.reportsReducer.salesBreakdownFulfillments
  );
  const locationId = yield select((state) => state.user.locationId);
  yield put(SET_REPORT_LOCATION(locationId));
  const {_id: userID} = yield select((store) => store.user.userData);
  const userRole = yield select((store) => store.user.userData.role);
  const selfDeliveryInvoicesPeriod = yield select(
    (state) => state.reportsReducer.selfDeliveryInvoicesPeriod
  );
  const timezone = yield select((state) => state.user.activeLocation?.Timezone);
  const requestRange =
    reportType === REPORT_TYPES.SELF_DELIVERY_INVOICES
      ? getSelfDeliveryInvoicesDateRange(selfDeliveryInvoicesPeriod, timezone)
      : dateRange;

  try {
    const report = yield generateReportApi(
      requestRange[0],
      requestRange[1],
      locationId,
      reportType,
      {
        channels: salesBreakdownChannels,
        fulfillments: salesBreakdownFulfillments
      },
      selfDeliveryDateFilter
    );

    if (reportType === 0) {
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          totalSummary: report.data.totalSummary,
          reportData: {
            dataRows: report.data.dailySummaries,
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: report.data.id,
          metadata: report.data.metadata
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(report?.data?.dailySummaries?.length);
      }
    }

    if (reportType === 1) {
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          reportData: {
            dataRows: report?.data?.summaries.sort((a, b) => {
              return (
                new Date(a.lastOrderCreationDate) -
                new Date(b.lastOrderCreationDate)
              );
            }),
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: report?.data?.id
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(report?.data?.summaries?.length);
      }
    }

    if (reportType === 2) {
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          reportData: {
            dataRows: report.data.payouts,
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: report.data.id,
          metadata: report.data.metadata
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(report?.data?.payouts?.length);
      }
    }

    if (reportType === 3) {
      // Self Delivery Summary report
      // Backend now calculates commissionSaved per order and totalCommissionSaved
      const orders = report.data.orders || [];
      const total = report.data.total || {};

      const parsedTotal = {
        totalChargeAmount: total.totalChargeAmount ?? 0,
        totalDeliveryFeesAndTipsPaidByCustomer:
          total.totalDeliveryFeesAndTipsPaidByCustomer ?? 0,
        totalDeliveryFeeOnlyPaidByCustomer:
          total.totalDeliveryFeeOnlyPaidByCustomer ?? 0,
        totalTipPaidByCustomer: total.totalTipPaidByCustomer ?? 0,
        totalDeliveryFeePaidByStore: total.totalDeliveryFeePaidByStore ?? 0,
        totalCreditReimbursement: total.totalCreditReimbursement ?? 0
      };

      const aggregatedRows = aggregateOrdersByDay(
        orders,
        selfDeliveryDateFilter
      );

      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          reportData: {
            dataRows: aggregatedRows,
            rawDataRows: orders,
            reportTypeInner: reportType,
            reportPeriod: dateRange,
            reportDateFilter: selfDeliveryDateFilter
          },
          totalRow: {
            ...parsedTotal,
            totalCommissionSaved:
              total.totalCommissionSaved != null
                ? Math.abs(total.totalCommissionSaved)
                : 0
          },
          reportId: report.data.id || null
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(orders?.length);
      }
    }

    if (reportType === 5) {
      const dataContent = yield getReportsApi(report.data.id, reportType);

      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          totalSummary: {totalAmount: report.data.totalAmount},
          reportData: {
            dataRows: dataContent.data.data,
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: report.data.id
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(dataContent?.data?.data?.length);
      }
    }

    if (reportType === 6) {
      const dataContent = yield getReportsApi(report.data.id, reportType);

      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          totalSummary: {totalAmount: report.data.totalAmount},
          reportData: {
            dataRows: dataContent.data.data,
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: report.data.id
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(dataContent?.data?.data?.length);
      }
    }

    if (reportType === 8) {
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          totalSummary: report.data.totalSummary,
          reportData: {
            dataRows: report.data.dailySummaries,
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: report.data.id
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(report?.data?.dailySummaries?.length);
      }
    }

    if (reportType === 9) {
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          totalSummary: report.data.totalSummary,
          reportData: {
            dataRows: report.data.dailySummaries,
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: report.data.id
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(report?.data?.dailySummaries?.length);
      }
    }

    if (reportType === REPORT_TYPES.SALES_BREAKDOWN_SUMMARY) {
      const dailySummaries = [...(report.data.dailySummaries || [])].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          totalSummary: report.data.totalSummary || {},
          reportData: {
            dataRows: dailySummaries,
            reportTypeInner: reportType,
            reportPeriod: dateRange,
            channels: salesBreakdownChannels || [],
            fulfillments: salesBreakdownFulfillments || []
          },
          reportId: null
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(dailySummaries.length);
      }
    }

    if (reportType === 10) {
      const orders = report.data.orders || [];
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          reportData: {
            dataRows: orders,
            reportTypeInner: reportType,
            reportPeriod: dateRange
          },
          reportId: null
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(orders.length);
      }
    }

    if (reportType === REPORT_TYPES.SELF_DELIVERY_INVOICES) {
      const invoices = report.data.invoices || [];
      yield putIfStillCurrent(
        revision,
        locationId,
        GENERATE_REPORT_SUCCESS({
          reportData: {
            dataRows: invoices,
            reportTypeInner: reportType,
            reportPeriod: requestRange
          },
          reportId: null
        })
      );
      if (payload?.successCallback) {
        payload?.successCallback(invoices.length);
      }
    }

    const employeeView = getSessionStorageItem(SESSION_SAUCE_EMPLOYEE_NAME);

    const {data} = yield getUserStatisticApi({
      userId: userID,
      tag: 'reports',
      sourceType: 'webApp',
      role: `${employeeView ? 'sudo' : userRole}`
    });

    // This also ends the loading state, so a stale request must not dispatch it.
    if ((yield select(selectReportRevision)) === revision) {
      yield put(SET_REPORT_VISITED_STATISTIC(data?.quantityData));
    }

    yield postUserStatisticApi({
      userID,
      url: report.config.url,
      tag: 'reports',
      sourceType: `${!window.AndroidDashboard ? 'webApp' : 'androidApp'}`,
      statusCode: report.status,
      role: `${employeeView ? 'sudo' : userRole}`
    });
  } catch (error) {
    yield stopLoadingIfStillCurrent(revision);
    console.log('SAGA ERROR', error);
  }
}

function* generateSpecialOrdersReport({payload}) {
  const revision = yield select(selectReportRevision);
  const dateRange = yield select((state) => state.reportsReducer.dateRange);
  const {userData} = yield select((store) => store.user);

  try {
    const {data} = yield getSpecialOrdersReportApi({
      merchantId: userData.merchant_id,
      period:
        !dateRange[0] && !dateRange[1]
          ? null
          : {
              from: dateRange[0],
              to: dateRange[1]
            },
      includeModifiers: payload.includeModifiers
    });
    if (data) {
      const response = yield addEmailToSpecialOrdersReportApi(
        userData.merchant_id,
        userData.email
      );
      if (response.status === 200) {
        yield putIfStillCurrent(
          revision,
          null,
          GENERATE_SPECIAL_ORDERS_REPORT_SUCCESS(data)
        );
      }
    }
  } catch (error) {
    yield stopLoadingIfStillCurrent(revision);
    console.log('SAGA ERROR', error);
  }
}
// This function may be used in future for BookADriver and BYOC reports:

// function* getReports() {
//   const reportType = yield select((state) => state.reportsReducer.reportType);
//   const skip = yield select((state) => state.reportsReducer.skip);
//   const reportId = yield select((state) => state.reportsReducer.reportId);
//
//   try {
//     const {data} = yield getReportsApi(reportId, skip, reportType);
//
//     if (data) {
//       yield put(GET_REPORTS_SUCCESS({data}));
//     }
//   } catch (error) {
//     yield put(STOP_REPORTS_LOADING());
//     console.log('SAGA ERROR', error);
//   }
// }

function* recalculateCommissionSaved(action) {
  const reportType = yield select((state) => state.reportsReducer.reportType);
  if (reportType === REPORT_TYPES.SELF_DELIVERY_INVOICES) {
    // Invoice rows are server-aggregated; regenerate with the saved rates.
    yield put(GENERATE_REPORT());
    return;
  }
  const data = yield select((state) => state.reportsReducer.data);
  const totalRow = yield select((state) => state.reportsReducer.totalRow);
  const dateRange = yield select((state) => state.reportsReducer.dateRange);
  const reportId = yield select((state) => state.reportsReducer.reportId);

  if (reportType !== 3 || !data?.dataRows?.length) {
    return;
  }

  const orders = data.rawDataRows || data.dataRows;
  const orderSourceToPlatform = {
    uber: 'UE',
    grubhub: 'GH',
    doordash: 'DD',
    slice: 'Slice'
  };

  const platformCommissions = (action.payload || []).map((item) => ({
    platform: item.platform,
    platformCommissionSelfDelivery: item.platformCommissionSelfDelivery,
    platformCommissionPlatformDelivery: item.platformCommissionPlatformDelivery
  }));

  if (!platformCommissions.length) {
    return;
  }

  const commissionsMap = {};
  platformCommissions.forEach((comm) => {
    commissionsMap[comm.platform] = comm;
  });

  const ordersWithCommission = orders.map((order) => {
    const platformCode =
      orderSourceToPlatform[order.orderSource?.toLowerCase()];
    let commissionSaved = null;

    if (platformCode && commissionsMap[platformCode]) {
      const commission = commissionsMap[platformCode];
      const marketplaceCommissionValue =
        commission.platformCommissionPlatformDelivery != null
          ? commission.platformCommissionPlatformDelivery
          : null;
      const selfDeliveryCommissionValue =
        commission.platformCommissionSelfDelivery != null
          ? commission.platformCommissionSelfDelivery
          : null;

      if (
        marketplaceCommissionValue != null &&
        selfDeliveryCommissionValue != null
      ) {
        const marketplaceCommission =
          parseFloat(marketplaceCommissionValue) / 100 || 0;
        const selfDeliveryCommission =
          parseFloat(selfDeliveryCommissionValue) / 100 || 0;
        const orderSubtotal = parseFloat(order.orderSubtotal) || 0;

        if (marketplaceCommission > 0 && selfDeliveryCommission >= 0) {
          const calculated =
            orderSubtotal * (marketplaceCommission - selfDeliveryCommission);
          commissionSaved = Math.abs(calculated);
        }
      }
    }

    return {
      ...order,
      commissionSaved
    };
  });

  const totalCommissionSaved = ordersWithCommission.reduce((sum, order) => {
    const commissionValue = order.commissionSaved;
    return sum + (commissionValue != null ? commissionValue : 0);
  }, 0);

  const aggregatedRows = aggregateOrdersByDay(
    ordersWithCommission,
    data.reportDateFilter
  );

  yield put(
    GENERATE_REPORT_SUCCESS({
      reportData: {
        dataRows: aggregatedRows,
        rawDataRows: ordersWithCommission,
        reportTypeInner: reportType,
        reportPeriod: dateRange,
        reportDateFilter: data.reportDateFilter
      },
      totalRow: {
        ...totalRow,
        totalCommissionSaved: Math.abs(totalCommissionSaved)
      },
      reportId: reportId || null
    })
  );
}

function* exportReports() {
  const reportId = yield select((state) => state.reportsReducer.reportId);
  const {reportTypeInner, reportPeriod, reportDateFilter} = yield select(
    (state) => state.reportsReducer.data
  );
  const dateRange = yield select((state) => state.reportsReducer.dateRange);
  const locationId = yield select((state) => state.user.locationId);
  const salesBreakdownChannels = yield select(
    (state) => state.reportsReducer.salesBreakdownChannels
  );
  const salesBreakdownFulfillments = yield select(
    (state) => state.reportsReducer.salesBreakdownFulfillments
  );
  const activeLocation = yield select((state) => state.user.activeLocation);

  const reportsNames = {
    0: 'Sales',
    2: 'Payout',
    3: 'Self Delivery Summary',
    5: 'Refunds',
    9: 'Discount',
    10: 'Cashback Usage',
    [REPORT_TYPES.SALES_BREAKDOWN_SUMMARY]: 'Orders By Channel Summary'
  };
  let formattedDate = 'all period';
  if (reportTypeInner !== 10 && (reportPeriod?.[0] || reportPeriod?.[1])) {
    const startDate = moment(reportPeriod[0]).format('M.D.YYYY');
    const endDate = moment(reportPeriod[1]).format('M.D.YYYY');
    formattedDate =
      startDate === endDate ? startDate : `${startDate}-${endDate}`;
  }

  let fileName = `Sauce ${reportsNames[reportTypeInner]} report for ${formattedDate}.xlsx`;
  if (reportTypeInner === 10) {
    const locationName = (
      activeLocation?.FullName ||
      activeLocation?.BusinessName ||
      'location'
    )
      .replace(/[<>:"/\\|?*\s]+/g, '_')
      .replace(/_+/g, '_');
    fileName = `cashback_usage_${locationName}_${moment().format(
      'YYYY-MM-DD'
    )}.xlsx`;
  }

  try {
    const resp = yield getXlsReportsApi(
      reportId,
      reportTypeInner,
      locationId,
      dateRange,
      {
        channels: salesBreakdownChannels,
        fulfillments: salesBreakdownFulfillments
      },
      reportDateFilter
    );

    if (resp) {
      // if there is a header with a filename, then we take the filename from it
      if (
        reportTypeInner !== 10 &&
        resp.headers['content-disposition'] &&
        resp.headers['content-disposition'].indexOf('filename="') > -1
      ) {
        const indexStart =
          resp.headers['content-disposition'].indexOf('filename="') + 10;
        const indexEnd = resp.headers['content-disposition'].indexOf(
          '"',
          indexStart
        );
        fileName = resp.headers['content-disposition'].substring(
          indexStart,
          indexEnd
        );
      }

      saveAs(new Blob([resp.data], {type: ''}), fileName);
      yield put(STOP_EXPORT_REPORTS_LOADING());
    }
  } catch (error) {
    console.log('SAGA ERROR', error);
    yield toast.error('Something went wrong');
    yield put(STOP_EXPORT_REPORTS_LOADING());
  }
}

const fileNameFromContentDisposition = (header) => {
  if (!header || header.indexOf('filename="') === -1) {
    return null;
  }
  const start = header.indexOf('filename="') + 10;
  const end = header.indexOf('"', start);
  return header.substring(start, end);
};

function* downloadSelfDeliveryInvoiceOrders({payload}) {
  const locationId = yield select((state) => state.user.locationId);
  const {stripeNumber, chargeDate} = payload;

  try {
    const resp = yield exportSelfDeliveryInvoiceOrdersCsvApi(
      locationId,
      stripeNumber,
      chargeDate
    );
    const fileName =
      fileNameFromContentDisposition(resp?.headers?.['content-disposition']) ||
      `Self Delivery Service Report ${stripeNumber}.csv`;
    saveAs(new Blob([resp.data], {type: 'text/csv'}), fileName);
  } catch (error) {
    console.log('SAGA ERROR', error);
    yield toast.error('Something went wrong');
  } finally {
    yield put(DOWNLOAD_SELF_DELIVERY_INVOICE_ORDERS_DONE());
  }
}

function* openSelfDeliveryInvoice({payload}) {
  const locationId = yield select((state) => state.user.locationId);
  const {stripeNumber} = payload;

  try {
    const resp = yield getSelfDeliveryInvoiceLinkApi(locationId, stripeNumber);
    const url = resp?.data?.invoicePdf;
    if (!url) {
      yield toast.info('Invoice PDF is not available in Stripe yet');
      return;
    }
    // Stripe serves invoicePdf as an attachment, so a same-tab link click downloads without leaving the page.
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.log('SAGA ERROR', error);
    if (error?.response?.status === 404) {
      yield toast.info('Invoice is not available in Stripe yet');
    } else {
      yield toast.error('Something went wrong');
    }
  } finally {
    yield put(OPEN_SELF_DELIVERY_INVOICE_DONE());
  }
}

function* reportsSaga() {
  yield takeEvery(EXPORT_REPORTS, exportReports);
  yield takeLatest(GENERATE_REPORT, generateReport);
  yield takeEvery(GENERATE_SPECIAL_ORDERS_REPORT, generateSpecialOrdersReport);
  yield takeEvery(RECALCULATE_COMMISSION_SAVED, recalculateCommissionSaved);
  yield takeEvery(
    DOWNLOAD_SELF_DELIVERY_INVOICE_ORDERS,
    downloadSelfDeliveryInvoiceOrders
  );
  yield takeEvery(OPEN_SELF_DELIVERY_INVOICE, openSelfDeliveryInvoice);
  // yield takeEvery(GET_REPORTS, getReports);
}

export default reportsSaga;
